import React, { useEffect, useState, useCallback } from 'react';
import { Container, Form, Row, Col, Table, Pagination, Button, Placeholder, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getAllTickerNotes, getTickers } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';

const styles = `
  .notes-card { background-color: var(--card-bg) !important; border: 2px solid var(--border-color) !important; box-shadow: 0 4px 16px var(--shadow-color) !important; }
  .notes-table-container { 
    background-color: var(--card-bg) !important; 
    border: 2px solid var(--border-color) !important; 
    border-radius: 8px 8px 0 0 !important; 
    box-shadow: 0 4px 16px var(--shadow-color) !important; 
    overflow: hidden !important; 
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
  }
  .notes-table thead th { background-color: var(--bg-secondary) !important; border-bottom: 2px solid var(--border-color) !important; font-weight: 600 !important; color: var(--text-primary) !important; padding: 6px 6px !important; }
  .notes-table tbody td { padding: 4px 6px !important; border-bottom: 1px solid var(--border-color) !important; vertical-align: middle !important; }
  
  /* Enhanced table styling with hover effects */
  .notes-table-container .notes-table tbody tr {
    border-bottom: 1px solid var(--border-color) !important;
    transition: all 0.3s ease-in-out !important;
    font-size: 0.95rem !important;
  }
  
  .notes-table-container .notes-table tbody tr:hover {
    background-color: var(--hover-bg) !important;
  }
  
  .notes-table-container .notes-table tbody tr:hover td {
    color: var(--text-primary) !important;
    font-weight: 500 !important;
  }
  
  /* Enhanced table cell styling */
  .notes-table-container .notes-table tbody td {
    padding: 4px 6px !important;
    border-bottom: 1px solid var(--border-color) !important;
    vertical-align: middle !important;
    transition: all 0.3s ease-in-out !important;
  }
  
  /* Fix table bottom alignment and remove horizontal scrollbar */
  .notes-table-container .table-responsive {
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
    overflow: hidden !important;
  }
  
  .notes-table-container .notes-table {
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
  }
  
  /* Ensure last row border is visible and table bottom aligns properly */
  .notes-table-container .notes-table tbody tr:last-child td {
    border-bottom: 1px solid var(--border-color) !important;
  }
  
  /* Remove the top border line that appears above the table header while keeping vertical borders */
  .notes-table.table-bordered thead th,
  .notes-table.table-bordered thead tr,
  .notes-table.table-bordered thead,
  .notes-table.table-bordered {
    border-top: 0 !important;
  }
  
  /* Also ensure the first header row cells do not render a top border */
  .notes-table.table-bordered thead tr:first-child th {
    border-top: 0 !important;
  }
  
  /* Two-column layout styles */
  .notes-two-column-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }
  
  .notes-column {
    background-color: var(--card-bg) !important;
    border: 2px solid var(--border-color) !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 16px var(--shadow-color) !important;
    overflow: hidden !important;
  }
  
  .notes-column-header {
    background-color: var(--bg-secondary) !important;
    border-bottom: 2px solid var(--border-color) !important;
    padding: 12px 16px !important;
    font-weight: 600 !important;
    color: var(--text-primary) !important;
    text-align: center !important;
  }
  
  .notes-column-table {
    margin-bottom: 0 !important;
  }
  
  .notes-column-table tbody tr:last-child td {
    border-bottom: none !important;
  }
  
  /* Ensure text truncation works properly without scrollbars */
  .notes-column-table td {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
  
  .notes-column-table td:last-child {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    max-width: 0 !important;
  }
  
  /* CSS Variables for theme support */
  :root {
    --card-bg: #ffffff;
    --border-color: #d1d5db;
    --shadow-color: rgba(0, 0, 0, 0.1);
    --text-primary: #374151;
    --bg-secondary: #f8f9fa;
    --hover-bg: #f1f5f9;
  }
  
  [data-theme="dark"] {
    --card-bg: #1f2937;
    --border-color: #4b5563;
    --shadow-color: rgba(0, 0, 0, 0.3);
    --text-primary: #f9fafb;
    --bg-secondary: #374151;
    --hover-bg: #4b5563;
  }
`;

function Notes() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [distinctTickerCount, setDistinctTickerCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [symbolFilter, setSymbolFilter] = useState('');

  useEffect(() => {
    fetchTickers();
  }, []);

  const fetchTickers = async () => {
    try {
      await getTickers();
    } catch (e) {
      // non-blocking
    }
  };

  const fetchNotes = useCallback(async () => {
    setTableLoading(true);
    setError('');
    try {
      const trimmed = symbolFilter.trim();
      // When filtering by symbol, the filtered view isn't paginated at all
      // (totalPages is hardcoded to 1 below, and the pagination controls are
      // hidden while a filter is active) -- so this needs to fetch
      // everything that matches, not just page 1 of a default-sized page.
      // A hardcoded limit of 1 here used to mean "filter by ticker" could
      // show at most a single note system-wide, no matter how many notes
      // that ticker (or other tickers matching a partial symbol) actually
      // had.
      const effectiveLimit = trimmed ? 1000 : pageSize * 4; // Get more notes since we're filtering to distinct tickers
      const effectivePage = trimmed ? 1 : currentPage;
      const resp = await getAllTickerNotes(effectivePage, effectiveLimit, trimmed);
      setNotes(resp.data.notes || []);
      if (trimmed) {
        setTotalPages(1);
        setDistinctTickerCount((resp.data.notes || []).length);
      } else {
        // Calculate distinct ticker count after grouping
        const grouped = (resp.data.notes || []).reduce((acc, note) => {
          if (!acc[note.symbol] || new Date(note.updated_at || note.created_at) > new Date(acc[note.symbol].updated_at || acc[note.symbol].created_at)) {
            acc[note.symbol] = note;
          }
          return acc;
        }, {});
        const distinctCount = Object.keys(grouped).length;
        setDistinctTickerCount(distinctCount);
        setTotalPages(Math.ceil(distinctCount / (pageSize * 2)) || 1);
      }
    } catch (e) {
      setError('Failed to load notes.');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [currentPage, pageSize, symbolFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchNotes();
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [fetchNotes]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); };
  const resetFilters = () => { setSymbolFilter(''); setCurrentPage(1); };

  // Group notes by ticker and get the latest note for each
  const groupedNotes = notes.reduce((acc, note) => {
    if (!acc[note.symbol] || new Date(note.updated_at || note.created_at) > new Date(acc[note.symbol].updated_at || acc[note.symbol].created_at)) {
      acc[note.symbol] = note;
    }
    return acc;
  }, {});
  
  // Convert to array and sort by most recent
  const distinctTickerNotes = Object.values(groupedNotes).sort((a, b) => 
    new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
  );
  
  // Split distinct ticker notes into left and right columns
  const leftColumnNotes = distinctTickerNotes.slice(0, pageSize);
  const rightColumnNotes = distinctTickerNotes.slice(pageSize, pageSize * 2);

  const renderPagination = () => {
    const items = [];
    const maxItems = 7;
    items.push(<Pagination.Item key={1} active={currentPage === 1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(totalPages - 1, maxItems - 2);
    else if (currentPage >= totalPages - 2) startPage = Math.max(2, totalPages - maxItems + 2);
    if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    for (let i = startPage; i <= endPage; i++) items.push(
      <Pagination.Item key={i} active={currentPage === i} onClick={() => handlePageChange(i)}>{i}</Pagination.Item>
    );
    if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    if (totalPages > 1) items.push(
      <Pagination.Item key={totalPages} active={currentPage === totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>
    );
    return (
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <span className="me-2">Rows per page:</span>
          <Form.Select size="sm" value={pageSize} onChange={handlePageSizeChange} style={{ width: '80px' }}>
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Form.Select>
          <span className="ms-3">
            {distinctTickerCount > 0 ? (
              <>Showing {(currentPage - 1) * (pageSize * 2) + 1} to {Math.min(currentPage * (pageSize * 2), distinctTickerCount)} of {distinctTickerCount} tickers with notes</>
            ) : 'No notes found'}
          </span>
        </div>
        <Pagination size="sm">
          <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => handlePageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1} />
          {items}
          <Pagination.Next onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    );
  };

  const renderPaginationPlaceholder = () => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="d-flex align-items-center">
        <Placeholder as="span" animation="glow" className="me-2"><Placeholder xs={6} /></Placeholder>
        <Placeholder as={Form.Select} animation="glow" style={{ width: '80px' }} />
        <Placeholder as="span" animation="glow" className="ms-3"><Placeholder xs={8} /></Placeholder>
      </div>
      <Placeholder.Button variant="primary" xs={5} />
    </div>
  );

  const renderNotesColumn = (columnNotes, columnTitle, columnKey) => (
    <div className="notes-column" key={columnKey}>
      <div className="table-responsive">
        <Table responsive hover className="align-middle notes-table notes-column-table" style={{ width: '100%', overflowX: 'auto' }} bordered>
                        <thead>
                <tr>
                  <th style={{ minWidth: '100px', width: '120px' }}>Date</th>
                  <th style={{ minWidth: '80px', width: '100px' }}>Symbol</th>
                  <th>Note</th>
                </tr>
              </thead>
          <tbody>
            {columnNotes.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-4">No notes</td>
              </tr>
            ) : (
              columnNotes.map(n => (
                                <tr key={n.id} role="button" style={{ cursor: 'pointer' }} onClick={() => navigate(`/notes/${encodeURIComponent(n.symbol)}`)}>
                        <td style={{ minWidth: '120px' }}>{formatDate(n.updated_at || n.created_at)}</td>
                  <td><span style={{ color: '#007bff', fontWeight: 'bold' }}>{n.symbol}</span></td>
                  <td>
                    {n.note ? (
                      <OverlayTrigger placement="top" overlay={<Tooltip id={`note-${n.id}`}>{n.note}</Tooltip>}>
                        <span className="text-truncate d-inline-block" style={{ maxWidth: '100%', overflow: 'hidden' }}>{n.note}</span>
                      </OverlayTrigger>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );

  return (
    <Container className="py-4">
      <style>{styles}</style>
      <div className="mb-4">
        <Form onSubmit={(e) => e.preventDefault()}>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Form.Group className="mb-0">
                {loading ? (
                  <Placeholder as={Form.Control} animation="glow" size="sm" />
                ) : (
                  <Form.Control
                    type="text"
                    value={symbolFilter}
                    onChange={(e) => { setSymbolFilter(e.target.value); setCurrentPage(1); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); } }}
                    placeholder="Enter ticker symbol to filter notes..."
                    size="sm"
                    className="border-0 shadow-sm"
                    style={{ backgroundColor: 'var(--bg-secondary)' }}
                  />
                )}
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="text-nowrap px-3" 
                onClick={resetFilters} 
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Clear
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {tableLoading ? (
        <div className="notes-table-container">
          <div className="table-responsive">
            <Table responsive className="align-middle notes-table" style={{ width: '100%', overflowX: 'auto' }} bordered>
              <thead>
                <tr>
                  <th style={{ minWidth: '100px', width: '120px' }}>Date</th>
                  <th style={{ minWidth: '80px', width: '100px' }}>Symbol</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {Array(pageSize).fill(0).map((_, idx) => (
                  <tr key={idx}>
                    <td><Placeholder animation="glow"><Placeholder xs={6} /></Placeholder></td>
                    <td><Placeholder animation="glow"><Placeholder xs={4} /></Placeholder></td>
                    <td><Placeholder animation="glow"><Placeholder xs={8} /></Placeholder></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      ) : (
        <div className="notes-two-column-container">
          {renderNotesColumn(leftColumnNotes, 'Left Column', 'left')}
          {renderNotesColumn(rightColumnNotes, 'Right Column', 'right')}
        </div>
      )}

      {symbolFilter.trim() ? null : (loading ? renderPaginationPlaceholder() : renderPagination())}
    </Container>
  );
}

export default Notes;


